import { TFunction } from 'react-i18next';

import { CreditCardBrand, MaybeNullable, Nullable } from '../../../types';

export type AdyenEnvironment = 'test' | 'live';

export function isAdyenEnvironment(value: unknown): value is AdyenEnvironment {
  return typeof value === 'string' && (value === 'test' || value === 'live');
}

export const RENDER_CONTAINER_ID = 'custom-card-container';

export const styles = {
  base: {
    // same as the one for .v3-form__input
    appearance: 'none',
    width: '100%',
    height: '100%',
    padding: '15px',
    minWidth: '0',
    color: '#555',
    lineHeight: '1.15',
    fontSize: '14px',
    fontFamily: 'sans-serif',
    fontWeight: '400',
  },
  error: {
    color: '#555',
  },
  validated: {
    color: '#555',
  },
};

// For the list of card brands supported on the web by Adyen: https://docs.adyen.com/payment-methods/cards/custom-card-integration#supported-card-types
// For the variants: https://docs.adyen.com/development-resources/paymentmethodvariant
type AdyenCardBrand = 'amex' | 'cartebancaire' | 'diners' | 'maestro' | 'mc' | 'visa';

const creditCardBrandOfAdyenBrand: Record<AdyenCardBrand, Nullable<CreditCardBrand>> = {
  amex: 'american_express',
  cartebancaire: 'carte_bancaire',
  diners: 'diners_club',
  maestro: 'maestro',
  mc: 'master_card',
  visa: 'visa',
};

export function normalizeAdyenCardBrand(value: unknown): Nullable<CreditCardBrand> {
  if (typeof value === 'string') {
    const creditCardBrand = (
      creditCardBrandOfAdyenBrand as Record<string, MaybeNullable<CreditCardBrand>>
    )[value];
    return creditCardBrand ?? null;
  } else {
    return null;
  }
}

const adyenBrandOfCreditCardBrand: Record<CreditCardBrand, Nullable<AdyenCardBrand>> = {
  american_express: 'amex',
  carte_bancaire: 'cartebancaire',
  diners_club: 'diners',
  maestro: 'maestro',
  master_card: 'mc',
  other: null,
  visa: 'visa',
};

export function getAdyenCardBrands(creditCardBrands: CreditCardBrand[]) {
  const adyenCardBrands = [];

  for (const creditCardBrand of creditCardBrands) {
    const adyenCardBrand = adyenBrandOfCreditCardBrand[creditCardBrand];
    if (adyenCardBrand) {
      adyenCardBrands.push(adyenCardBrand);
    }
  }

  return adyenCardBrands;
}

// this is used to override adyen default i18n text,  this is also recommended by adyen to disable placeholders on the fields
// see https://docs.adyen.com/online-payments/web-components/localization-components
export function overrideAdyenTranslations(
  t: TFunction<'translation', undefined>,
): Record<string, string> {
  const requiredMessage = t('checkout.form.required.error');
  const cardNotSupported = t('checkout.payment.creditcard.type_not_supported');
  const invalidDate = t('checkout.payment.creditcard.expiry_date.invalid');

  return {
    'error.va.sf-cc-num.01': cardNotSupported,
    'error.va.sf-cc-num.02': requiredMessage,
    'error.va.sf-cc-num.03': cardNotSupported,
    'error.va.sf-cc-num.04': t('checkout.form.size.error', { '0': '4', '1': '23' }),
    'error.va.sf-cc-dat.01': invalidDate,
    'error.va.sf-cc-dat.02': invalidDate,
    'error.va.sf-cc-dat.03': invalidDate,
    'error.va.sf-cc-dat.04': requiredMessage,
    'error.va.sf-cc-dat.05': invalidDate,
    'error.va.sf-cc-mth.01': requiredMessage,
    'error.va.sf-cc-yr.01': requiredMessage,
    'error.va.sf-cc-yr.02': invalidDate,
    'error.va.sf-cc-cvc.01': requiredMessage,
    'error.va.sf-cc-cvc.02': t('checkout.form.invalid.error'),
    // emptying all placeholders
    'creditCard.numberField.placeholder': '',
    'creditCard.expiryDateField.placeholder': '',
    'creditCard.expiryDateField.month.placeholder': '',
    'creditCard.expiryDateField.year.placeholder': '',
    'creditCard.cvcField.placeholder': '',
    'creditCard.cvcField.placeholder.4digits': '',
    'creditCard.cvcField.placeholder.3digits': '',
  };
}
